import {announce} from '@github-ui/aria-live'
import {INVALID_ARTIFACT_NAME_MESSAGE, isValidArtifactName} from '@github-ui/scribe-artifacts/artifact-name'
import {PencilIcon} from '@primer/octicons-react'
import {Banner, FormControl, Text, Textarea, TextInput} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
  type PointerEvent,
  useCallback,
  useId,
  useRef,
  useState,
} from 'react'

import type {CreateArtifactDialogProps} from './create-artifact-dialog-types'
import styles from './CreateArtifactDialog.module.css'
import {useCreateArtifactSubmit} from './use-create-artifact-submit'

const CREATE_ARTIFACT_DIALOG_TITLE = 'Create new artifact'
const EDIT_ARTIFACT_DIALOG_TITLE = 'Edit artifact'
const CREATE_ARTIFACT_BODY_PLACEHOLDER = 'Write markdown...'
const MAX_DERIVED_ARTIFACT_NAME_LENGTH = 100

// First non-empty line of the body, with any leading `#` heading marker stripped.
export function deriveArtifactName(body: string): string {
  const firstLine =
    body
      .trim()
      .split(/\r?\n/)
      .find(line => line.trim())
      ?.trim() ?? ''
  return firstLine.replace(/^#{1,6}\s+/, '').slice(0, MAX_DERIVED_ARTIFACT_NAME_LENGTH)
}

export function CreateArtifactDialog({
  resourceId,
  onClose,
  onCreated,
  onUpdated,
  returnFocusRef,
  artifact,
  creationType = 'generic',
}: CreateArtifactDialogProps) {
  const formId = useId()
  const nameValidationMessageId = useId()
  const markdownInputRef = useRef<HTMLTextAreaElement>(null)
  const htmlTitleInputRef = useRef<HTMLInputElement>(null)
  const isHTMLCreation = !artifact && creationType === 'embedded_html'
  const titleBeforeEditRef = useRef<string | null>(null)
  // Set when edit mode ends via keyboard (Enter/Escape) so focus returns to the
  // title button as it remounts. Blur is user-driven and must not pull focus back.
  const returnFocusToTitleButtonRef = useRef(false)
  const [body, setBody] = useState(artifact?.body ?? '')
  // null means "follow the auto-generated title"; a string means the user has
  // taken over the title manually.
  const [manualTitle, setManualTitle] = useState<string | null>(artifact?.name ?? (isHTMLCreation ? '' : null))
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [nameValidationError, setNameValidationError] = useState('')
  const {clearCreateErrorMessage, createErrorMessage, isPending, submitArtifact} = useCreateArtifactSubmit({
    resourceId,
    artifactNumber: artifact?.number,
    onClose,
    onCreated,
    onUpdated,
    errorMessage: artifact ? 'Artifact could not be updated. Try again.' : 'Artifact could not be created. Try again.',
  })

  const dialogTitle = artifact
    ? EDIT_ARTIFACT_DIALOG_TITLE
    : isHTMLCreation
      ? 'Create HTML artifact'
      : CREATE_ARTIFACT_DIALOG_TITLE
  const submitButtonContent = artifact ? 'Save changes' : 'Create artifact'
  const loadingAnnouncement = artifact ? 'Saving artifact' : 'Creating artifact'
  const derivedTitle = isHTMLCreation ? '' : deriveArtifactName(body)
  const effectiveTitle = manualTitle ?? derivedTitle
  const bodyIsEmpty = body.trim().length === 0
  const dialogErrorMessage = nameValidationError || createErrorMessage

  const beginEditingTitle = useCallback(() => {
    // Snapshot the current title so Escape can revert to it.
    titleBeforeEditRef.current = manualTitle
    setIsEditingTitle(true)
  }, [manualTitle])

  const commitTitle = useCallback(() => {
    setIsEditingTitle(false)
    setManualTitle(prev => (prev !== null && prev.trim() === '' ? null : prev))
  }, [])

  const cancelEditingTitle = useCallback(() => {
    const restoredManualTitle = titleBeforeEditRef.current
    const restoredTitle = restoredManualTitle ?? derivedTitle
    setManualTitle(restoredManualTitle)
    setNameValidationError(isValidArtifactName(restoredTitle.trim()) ? '' : INVALID_ARTIFACT_NAME_MESSAGE)
    setIsEditingTitle(false)
  }, [derivedTitle])

  // Callback ref on the title button. After a keyboard commit/cancel the <input>
  // unmounts in favor of this <button>; move focus to it as it remounts so
  // keyboard users keep their place instead of being dropped on document.body
  // and bounced by the dialog's focus trap. Guarded by the flag so a user-driven
  // blur does not pull focus back.
  const titleButtonRef = useCallback((node: HTMLButtonElement | null) => {
    if (node && returnFocusToTitleButtonRef.current) {
      returnFocusToTitleButtonRef.current = false
      node.focus()
    }
  }, [])

  const handleTitleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.key === 'Enter') {
        event.preventDefault()
        returnFocusToTitleButtonRef.current = true
        commitTitle()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      } else if (event.key === 'Escape') {
        // Keep Escape from bubbling up and closing the whole dialog, and revert
        // to the pre-edit title rather than keeping the live keystrokes.
        event.stopPropagation()
        returnFocusToTitleButtonRef.current = true
        cancelEditingTitle()
      }
    },
    [commitTitle, cancelEditingTitle],
  )

  const handleTitleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setManualTitle(event.currentTarget.value)
      setNameValidationError('')
      clearCreateErrorMessage()
    },
    [clearCreateErrorMessage],
  )

  const handleTitlePointerDown = useCallback(
    (event: PointerEvent<HTMLButtonElement>) => {
      event.preventDefault()
      beginEditingTitle()
    },
    [beginEditingTitle],
  )

  const handleBodyChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setBody(event.currentTarget.value)
      setNameValidationError('')
      clearCreateErrorMessage()
    },
    [clearCreateErrorMessage],
  )

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      if (bodyIsEmpty) return

      const trimmedTitle = effectiveTitle.trim()
      if (!isValidArtifactName(trimmedTitle)) {
        setNameValidationError(INVALID_ARTIFACT_NAME_MESSAGE)
        setManualTitle(effectiveTitle)
        if (!isHTMLCreation) {
          setIsEditingTitle(true)
        }
        announce(INVALID_ARTIFACT_NAME_MESSAGE, {assertive: true})
        return
      }

      submitArtifact({
        name: trimmedTitle,
        body,
        type: artifact?.type ?? creationType,
      })
    },
    [artifact, bodyIsEmpty, effectiveTitle, body, creationType, isHTMLCreation, submitArtifact],
  )

  const titleNode = isEditingTitle ? (
    <>
      <input
        className={styles.TitleInput}
        aria-label="Artifact title"
        autoFocus
        value={effectiveTitle}
        placeholder={dialogTitle}
        disabled={isPending}
        aria-invalid={nameValidationError ? true : undefined}
        aria-describedby={nameValidationError ? nameValidationMessageId : undefined}
        onChange={handleTitleChange}
        onFocus={event => {
          // Defer the select() layout read out of the focus handler so selecting
          // the existing title text doesn't block the next paint (INP).
          const input = event.currentTarget
          requestAnimationFrame(() => input.select())
        }}
        onBlur={commitTitle}
        onKeyDown={handleTitleKeyDown}
      />
      {nameValidationError ? (
        <span id={nameValidationMessageId} className="sr-only">
          {nameValidationError}
        </span>
      ) : null}
    </>
  ) : (
    <>
      {artifact && <span className="sr-only">Edit artifact: </span>}
      <button
        type="button"
        ref={titleButtonRef}
        className={styles.TitleButton}
        onClick={beginEditingTitle}
        onPointerDown={artifact ? handleTitlePointerDown : undefined}
      >
        <span className={styles.TitleText}>{effectiveTitle || dialogTitle}</span>
        <PencilIcon size={14} className={styles.TitlePencil} />
      </button>
    </>
  )

  return (
    <Dialog
      title={isHTMLCreation ? dialogTitle : titleNode}
      className={clsx(styles.CreateDialog, dialogErrorMessage && styles.CreateDialogHasBanner)}
      width="xlarge"
      height="large"
      onClose={onClose}
      initialFocusRef={isHTMLCreation ? htmlTitleInputRef : markdownInputRef}
      returnFocusRef={returnFocusRef}
      renderBody={({children}) => <Dialog.Body className={styles.DialogBody}>{children}</Dialog.Body>}
      footerButtons={[
        {content: 'Cancel', onClick: onClose, buttonType: 'normal', disabled: isPending},
        {
          content: submitButtonContent,
          buttonType: 'primary',
          type: 'submit',
          form: formId,
          disabled: bodyIsEmpty || isPending,
          loading: isPending,
          loadingAnnouncement,
        },
      ]}
    >
      <form
        id={formId}
        aria-label={artifact ? 'Edit artifact' : isHTMLCreation ? 'Create HTML artifact' : 'Create artifact'}
        onSubmit={handleSubmit}
        className={styles.Form}
        noValidate={isHTMLCreation}
      >
        {dialogErrorMessage && (
          <Banner
            title={
              nameValidationError
                ? 'Invalid artifact name'
                : artifact
                  ? 'Unable to update artifact'
                  : 'Unable to create artifact'
            }
            variant="critical"
            description={dialogErrorMessage}
          />
        )}
        {isHTMLCreation ? (
          <>
            <Text as="p" size="small" className={styles.RequiredFieldsNote}>
              <span className={styles.RequiredMarker} aria-hidden="true">
                *
              </span>{' '}
              Required
            </Text>
            <FormControl required disabled={isPending}>
              <FormControl.Label className={styles.FormControlLabel}>Artifact title</FormControl.Label>
              <TextInput ref={htmlTitleInputRef} value={effectiveTitle} block onChange={handleTitleChange} />
              <FormControl.Caption>
                Use up to 255 characters: letters, numbers, spaces, periods, hyphens, or underscores. Start and end with
                a letter or number.
              </FormControl.Caption>
              {nameValidationError && (
                <FormControl.Validation variant="error">{nameValidationError}</FormControl.Validation>
              )}
            </FormControl>
            <FormControl required disabled={isPending} className={styles.HTMLBody}>
              <FormControl.Label className={styles.FormControlLabel}>HTML source</FormControl.Label>
              <Textarea
                value={body}
                placeholder="Paste HTML source..."
                block
                resize="none"
                className={styles.Textarea}
                onChange={handleBodyChange}
              />
            </FormControl>
          </>
        ) : (
          <Textarea
            ref={markdownInputRef}
            value={body}
            aria-label="Markdown"
            placeholder={CREATE_ARTIFACT_BODY_PLACEHOLDER}
            block
            resize="none"
            disabled={isPending}
            className={styles.Textarea}
            onChange={handleBodyChange}
          />
        )}
      </form>
    </Dialog>
  )
}
